import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TotalRewardsPerk } from "src/Pages/Company/TotalReward/models/TotalRewardsPerk.models";
import sortByField from "src/utils/jsHelpers/sortByField";
import { deletePerk, fetchPerk, fetchPerks, savePerk } from ".";

interface ListPerksProps {
  filters: object;
  page: number;
  itemsPerPage: number;
  order: object;
}

export const useListTotalRewardsPerks = ({
  filters = {},
  page,
  itemsPerPage,
  order = {},
}: ListPerksProps) =>
  useQuery({
    queryKey: [
      "total-rewards-perk",
      "list",
      { page, itemsPerPage, ...order, ...filters },
    ],
    queryFn: async ({ signal }) =>
      fetchPerks(
        {
          ...filters,
          page,
          itemsPerPage,
          order,
        },
        { signal }
      ),
  });

export const useListTotalRewardsPerksOptions = (companyId: string) =>
  useQuery({
    queryKey: ["total-rewards-perk", "options", { companyId }],
    queryFn: async ({ signal }) =>
      fetchPerks(
        {
          tenant: companyId,
          pagination: false,
        },
        { signal }
      ),
    select: (data) =>
      sortByField(
        data?.member.map((o) => ({
          label: o.title,
          value: o.id,
        })) || [],
        "label"
      ),
  });

export const useGetTotalRewardsPerk = (perkId: string) =>
  useQuery({
    enabled: perkId !== "-1",
    queryKey: ["total-rewards-perk", "details", { perkId }],
    queryFn: async ({ signal }) => fetchPerk(perkId, { signal }),
  });

export const useSaveTotalRewardsPerk = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: TotalRewardsPerk) =>
      savePerk(formData.id, formData),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["total-rewards-perk"],
        }),
      ]),
  });
};

export const useDeleteTotalRewardsPerk = (companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (perkId: string) => deletePerk(perkId, companyId),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["total-rewards-perk"],
        }),
      ]),
  });
};
